import type { BeadColor } from '../catalog/types';
import { makeNearestResolver } from './find-nearest';
import type { Grid, QuantizationResult } from './types';

/**
 * Quantification avec dithering par diffusion d'erreur Floyd-Steinberg.
 *
 * Référence : Floyd, R.W. & Steinberg, L. (1976), "An adaptive algorithm for
 * spatial grayscale," Proceedings of the SID 17(2).
 *
 * Diffusion de l'erreur sur les voisins non encore traités :
 *
 *           X    7/16
 *   3/16  5/16  1/16
 *
 * X = pixel courant, scanline gauche→droite haut→bas.
 *
 * L'erreur est calculée en RGB direct (canal par canal) après quantification.
 * Le pixel courant est clampé à [0, 255] avant la recherche du nearest neighbor,
 * mais l'erreur propagée n'est pas re-clampée (laisse les pixels suivants la
 * compenser naturellement).
 *
 * Extensions Lot P2 :
 *  - `strength` ∈ [0, 1] : intensité du tramage — multiplie l'erreur diffusée.
 *    1 = Floyd-Steinberg classique, 0 = équivalent nearest (aucune diffusion).
 *    C'est le slider « Détails » du tunnel (jamais nommé "dithering" en UI).
 *  - `allowed` : restreint la recherche à un sous-ensemble d'indices de la
 *    palette (réduction du nombre de couleurs, cf. reduce.ts).
 *
 * @throws si la palette est vide ou si la grille est incohérente.
 */
export function dither(
  grid: Grid,
  palette: ReadonlyArray<BeadColor>,
  strength = 1,
  allowed?: ReadonlyArray<number>,
): QuantizationResult {
  if (palette.length === 0) {
    throw new Error('Quantification impossible : palette vide.');
  }
  const s = clampStrength(strength);
  const N = grid.width * grid.height;
  if (grid.pixels.length !== N) {
    throw new Error(
      `Grille incohérente : ${grid.pixels.length} pixels, attendu ${N} (${grid.width}×${grid.height}).`,
    );
  }

  // Buffer R,G,B mutable en flottant : permet de cumuler l'erreur sous-pixellaire.
  const buffer = new Float64Array(N * 3);
  for (let i = 0; i < N; i++) {
    const p = grid.pixels[i];
    if (p === undefined) {
      throw new Error(`Pixel manquant à l'index ${i}.`);
    }
    buffer[i * 3] = p.r;
    buffer[i * 3 + 1] = p.g;
    buffer[i * 3 + 2] = p.b;
  }

  const cells: number[] = new Array<number>(N);
  const counts = new Map<string, number>();
  for (const c of palette) {
    counts.set(c.id, 0);
  }

  const W = grid.width;
  const H = grid.height;

  // Résolveur mémoïsé (créé une fois) : le nearest CIEDE2000 domine le coût.
  const resolve = makeNearestResolver(palette, allowed);

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const base = i * 3;

      // Lecture clampée du pixel courant (erreur diffusée peut sortir de [0, 255]).
      const r = clamp255(buffer[base] ?? 0);
      const g = clamp255(buffer[base + 1] ?? 0);
      const b = clamp255(buffer[base + 2] ?? 0);

      const idx = resolve(r, g, b);
      cells[i] = idx;
      const matched = palette[idx];
      if (matched === undefined) {
        throw new Error(`Index ${idx} hors palette (longueur ${palette.length}).`);
      }
      counts.set(matched.id, (counts.get(matched.id) ?? 0) + 1);

      const errR = (r - matched.rgb.r) * s;
      const errG = (g - matched.rgb.g) * s;
      const errB = (b - matched.rgb.b) * s;

      // Diffusion sur les 4 voisins non encore traités.
      if (x + 1 < W) {
        diffuse(buffer, (i + 1) * 3, errR, errG, errB, 7 / 16);
      }
      if (y + 1 < H) {
        const belowLeft = ((y + 1) * W + x - 1) * 3;
        const below = ((y + 1) * W + x) * 3;
        const belowRight = ((y + 1) * W + x + 1) * 3;
        if (x > 0) diffuse(buffer, belowLeft, errR, errG, errB, 3 / 16);
        diffuse(buffer, below, errR, errG, errB, 5 / 16);
        if (x + 1 < W) diffuse(buffer, belowRight, errR, errG, errB, 1 / 16);
      }
    }
  }

  return { width: W, height: H, cells, counts };
}

function clamp255(v: number): number {
  if (v < 0) return 0;
  if (v > 255) return 255;
  return v;
}

function clampStrength(v: number): number {
  if (!Number.isFinite(v)) return 1;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function diffuse(
  buf: Float64Array,
  offset: number,
  errR: number,
  errG: number,
  errB: number,
  weight: number,
): void {
  buf[offset] = (buf[offset] ?? 0) + errR * weight;
  buf[offset + 1] = (buf[offset + 1] ?? 0) + errG * weight;
  buf[offset + 2] = (buf[offset + 2] ?? 0) + errB * weight;
}
